/**
 * PREVIEW PANEL
 *
 * Browser-like panel with toolbar and iframe, styled with Colony design system.
 * Two modes:
 * - Cloud: connects to backend preview environment lifecycle APIs
 * - Local: starts a dev server on the local machine (Tauri only)
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useWorldStore } from "@/stores/worldStore";
import { usePreviewStore, type PreviewStatus } from "@/stores/previewStore";
import { useLocalPreviewStore } from "@/stores/localPreviewStore";
import { applySubdirectoryToCommand, resolveProjectPath } from "./projectPath";
import {
  Globe,
  RotateCw,
  ExternalLink,
  Monitor,
  Tablet,
  Smartphone,
  Loader2,
  Play,
  Square,
  RefreshCcw,
  Trash2,
  Search,
  ChevronDown,
  ChevronUp,
  Lock,
} from "lucide-react";

interface PreviewPanelProps {
  isLocalMode?: boolean;
  selectedProject?: import("@/stores/worldStore").Project;
  localProjects?: import("@/stores/worldStore").Project[];
  onSelectProject?: (id: string) => void;
}

// ── Colony Select (lightweight dropdown) ─────────────────────────────

interface ColonySelectOption {
  value: string;
  label: string;
}

function ColonySelect({
  value,
  options,
  placeholder,
  onChange,
  disabled,
  className = "",
}: {
  value: string;
  options: ColonySelectOption[];
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        className="flex items-center h-8 rounded-[6px] bg-[#151518] border border-colony-border px-3 min-w-[140px] cursor-pointer hover:border-colony-border-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full"
      >
        <span className={`text-[13px] flex-1 text-left truncate ${selected ? "text-colony-text-primary" : "text-colony-text-tertiary"}`}>
          {selected?.label ?? placeholder ?? "Select..."}
        </span>
        <ChevronDown size={14} className="text-colony-text-tertiary shrink-0 ml-2" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 w-full min-w-[160px] py-1 rounded-[8px] border border-colony-border bg-colony-surface shadow-[var(--shadow-elevated)] max-h-[200px] overflow-y-auto">
          {options.map((o) => (
            <button
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 h-8 text-[13px] hover:bg-colony-surface-elevated transition-colors cursor-pointer truncate ${
                o.value === value ? "text-colony-brand" : "text-colony-text-primary"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── URL Bar ──────────────────────────────────────────────────────────

type DeviceMode = "desktop" | "tablet" | "mobile";

function UrlBar({
  url,
  deviceMode,
  onDeviceChange,
}: {
  url: string;
  deviceMode: DeviceMode;
  onDeviceChange: (mode: DeviceMode) => void;
}) {
  const devices: { mode: DeviceMode; icon: React.ReactNode; label: string }[] = [
    { mode: "desktop", icon: <Monitor size={13} strokeWidth={1.5} />, label: "Desktop" },
    { mode: "tablet", icon: <Tablet size={13} strokeWidth={1.5} />, label: "Tablet" },
    { mode: "mobile", icon: <Smartphone size={13} strokeWidth={1.5} />, label: "Mobile" },
  ];

  return (
    <div className="flex items-center gap-2 h-[36px] px-3 bg-[#131316] border-b border-[rgba(255,255,255,0.06)] shrink-0">
      {/* Traffic lights */}
      <div className="flex items-center gap-1.5 mr-2 shrink-0">
        <span className="w-[10px] h-[10px] rounded-full bg-[hsl(0,70%,50%)] opacity-60" />
        <span className="w-[10px] h-[10px] rounded-full bg-[hsl(45,80%,55%)] opacity-60" />
        <span className="w-[10px] h-[10px] rounded-full bg-[hsl(120,50%,45%)] opacity-60" />
      </div>

      {/* URL field */}
      <div className="flex items-center gap-1.5 flex-1 h-[26px] px-2.5 rounded-[5px] bg-[#151518] border border-[#2A2A2F]">
        <Lock size={10} strokeWidth={1.5} className="text-colony-text-tertiary shrink-0" />
        <span className="text-[11px] font-mono text-colony-text-secondary truncate">
          {url}
        </span>
      </div>

      {/* Device toggles */}
      <div className="flex items-center gap-0.5 ml-2 shrink-0">
        {devices.map((d) => (
          <button
            key={d.mode}
            onClick={() => onDeviceChange(d.mode)}
            className={`flex items-center justify-center w-7 h-7 rounded-[4px] transition-all cursor-pointer ${
              deviceMode === d.mode
                ? "text-colony-brand bg-colony-brand/12"
                : "text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-[#222226]"
            }`}
            title={d.label}
          >
            {d.icon}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Empty/Stopped State ──────────────────────────────────────────────

function StoppedState({ message }: { message: string }) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center bg-[#0F0F11]">
      <div className="flex flex-col items-center gap-4 opacity-40">
        <Monitor size={48} strokeWidth={1} className="text-colony-text-secondary" />
        <span className="text-[14px] text-colony-text-secondary font-medium max-w-[360px] text-center">
          {message}
        </span>
      </div>
    </div>
  );
}

// ── Cloud Preview Panel ──────────────────────────────────────────────

function CloudPreviewPanel() {
  const projects = useWorldStore((s) => s.projects);
  const {
    environments,
    loading,
    mutating,
    errors,
    fetchEnvironment,
    startEnvironment,
    stopEnvironment,
    deleteEnvironment,
    clearError,
  } = usePreviewStore();

  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [branch, setBranch] = useState("main");
  const [isLoadingFrame, setIsLoadingFrame] = useState(false);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>("desktop");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const projectsWithRepo = useMemo(
    () => projects.filter((p) => p.githubRepo),
    [projects]
  );

  const selectedProject = projectsWithRepo.find(
    (p) => p.id.toString() === selectedProjectId
  );
  const selectedProjectNum = selectedProject ? selectedProject.id : null;

  const env = selectedProjectNum ? environments[selectedProjectNum] : undefined;
  const envLoading = selectedProjectNum ? Boolean(loading[selectedProjectNum]) : false;
  const envMutating = selectedProjectNum ? Boolean(mutating[selectedProjectNum]) : false;
  const envError = selectedProjectNum ? errors[selectedProjectNum] : null;

  const status: PreviewStatus = env?.status || "not_created";
  const previewUrl = env?.previewUrl || "";
  const canLoadPreview = status === "running" && Boolean(previewUrl);
  const hasSelection = Boolean(selectedProjectNum);

  useEffect(() => {
    if (!selectedProjectNum) return;
    clearError(selectedProjectNum);
    void fetchEnvironment(selectedProjectNum).catch(() => {
      setIsLoadingFrame(false);
    });
  }, [selectedProjectNum, fetchEnvironment, clearError]);

  useEffect(() => {
    if (!env?.branch) return;
    setBranch(env.branch);
  }, [env?.branch]);

  const handleRefreshFrame = () => {
    if (!iframeRef.current || !canLoadPreview) return;
    setIsLoadingFrame(true);
    iframeRef.current.src = previewUrl;
  };

  const handleOpenExternal = () => {
    if (!canLoadPreview) return;
    window.open(previewUrl, "_blank");
  };

  const handleRefreshStatus = async () => {
    if (!selectedProjectNum) return;
    await fetchEnvironment(selectedProjectNum).catch(() => {
      setIsLoadingFrame(false);
    });
  };

  const handleStart = async () => {
    if (!selectedProjectNum) return;
    setIsLoadingFrame(true);
    await startEnvironment(selectedProjectNum, branch.trim() || "main").catch(() => {
      setIsLoadingFrame(false);
    });
  };

  const handleStop = async () => {
    if (!selectedProjectNum) return;
    setIsLoadingFrame(false);
    await stopEnvironment(selectedProjectNum);
  };

  const handleDelete = async () => {
    if (!selectedProjectNum) return;
    setIsLoadingFrame(false);
    await deleteEnvironment(selectedProjectNum);
  };

  const projectOptions = projectsWithRepo.map((p) => ({
    value: p.id.toString(),
    label: p.name,
  }));

  const deviceWidths: Record<DeviceMode, number | null> = {
    desktop: null,
    tablet: 768,
    mobile: 375,
  };

  return (
    <div className="flex flex-col h-full bg-[var(--background)]">
      {/* Toolbar */}
      <div className="flex items-center gap-2 h-12 px-4 border-b border-colony-border bg-[var(--background)] shrink-0">
        {/* Project Selector */}
        <ColonySelect
          value={selectedProjectId}
          options={projectOptions}
          placeholder="Select project..."
          onChange={setSelectedProjectId}
          className="w-[180px]"
        />

        {/* Branch input */}
        <input
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          placeholder="main"
          aria-label="Branch name"
          disabled={!hasSelection || envMutating}
          className="h-8 px-3 rounded-[6px] bg-[#151518] border border-colony-border focus:border-colony-brand/50 focus:shadow-[0_0_0_3px_rgba(0,178,200,0.1)] text-[13px] text-colony-text-primary placeholder:text-colony-text-tertiary disabled:opacity-50 outline-none transition-all w-[120px] shrink-0"
        />

        <div className="w-px h-4 bg-colony-border mx-1" />

        {/* Status */}
        {status === "running" && (
          <span className="text-[12px] text-colony-green flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-colony-green" />
            Running
          </span>
        )}
        {envMutating && (
          <span className="text-[12px] text-colony-amber flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" />
            Working...
          </span>
        )}

        <div className="flex-1" />

        {/* Actions */}
        <button
          onClick={handleRefreshStatus}
          disabled={!hasSelection || envLoading || envMutating}
          className="w-8 h-8 flex items-center justify-center rounded-[6px] text-colony-text-tertiary hover:text-colony-text-primary hover:bg-colony-surface-elevated transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          title="Refresh status"
        >
          <RefreshCcw size={14} />
        </button>

        <button
          onClick={handleStart}
          disabled={!hasSelection || envLoading || envMutating || status === "running"}
          className="flex items-center gap-1.5 h-8 px-3 rounded-[6px] bg-colony-brand hover:bg-colony-brand-hover text-white text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] shrink-0"
        >
          <Play size={14} fill="currentColor" />
          Start
        </button>

        <button
          onClick={handleStop}
          disabled={!hasSelection || envLoading || envMutating || status !== "running"}
          className="flex items-center gap-1.5 h-8 px-3 rounded-[6px] text-colony-text-tertiary border border-colony-border hover:text-colony-text-primary hover:bg-colony-surface-elevated transition-all font-medium text-[13px] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer shrink-0"
        >
          <Square size={14} />
          Stop
        </button>

        <button
          onClick={handleDelete}
          disabled={!hasSelection || envLoading || envMutating || !env?.exists}
          className="flex items-center gap-1.5 h-8 px-3 rounded-[6px] bg-colony-red/10 text-colony-red border border-colony-red/20 hover:bg-colony-red/20 transition-all font-medium text-[13px] disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer shrink-0"
        >
          <Trash2 size={14} />
          Delete
        </button>

        {canLoadPreview && (
          <>
            <div className="w-px h-4 bg-colony-border mx-1" />
            <button
              onClick={handleRefreshFrame}
              className="w-8 h-8 flex items-center justify-center rounded-[6px] text-colony-text-tertiary hover:text-colony-text-primary hover:bg-colony-surface-elevated transition-colors"
              title="Refresh preview"
            >
              <RotateCw size={14} />
            </button>
            <button
              onClick={handleOpenExternal}
              className="w-8 h-8 flex items-center justify-center rounded-[6px] text-colony-text-tertiary hover:text-colony-text-primary hover:bg-colony-surface-elevated transition-colors"
              title="Open in browser"
            >
              <ExternalLink size={14} />
            </button>
          </>
        )}
      </div>

      {/* Error */}
      {envError && (
        <div className="mx-4 mt-3 rounded-[6px] border border-colony-red/30 bg-colony-red/5 px-3 py-2 text-[12px] text-colony-red">
          {envError}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 relative flex flex-col min-h-0">
        {(envLoading || envMutating || isLoadingFrame) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[#0F0F11]/80">
            <Loader2 className="w-6 h-6 animate-spin text-colony-text-tertiary" />
          </div>
        )}

        {canLoadPreview ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <UrlBar url={previewUrl} deviceMode={deviceMode} onDeviceChange={setDeviceMode} />
            <div
              className="flex-1 overflow-auto flex justify-center"
              style={{
                backgroundColor: "#0F0F11",
                backgroundImage: "radial-gradient(circle at 1px 1px, #1E1E22 1px, transparent 0)",
                backgroundSize: "20px 20px",
              }}
            >
              <div
                className="h-full bg-white overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  width: deviceWidths[deviceMode] ? `${deviceWidths[deviceMode]}px` : "100%",
                  maxWidth: "100%",
                  ...(deviceWidths[deviceMode]
                    ? { borderLeft: "1px solid #2A2A2F", borderRight: "1px solid #2A2A2F", boxShadow: "0 0 40px rgba(0,0,0,0.3)" }
                    : {}),
                }}
              >
                <iframe
                  ref={iframeRef}
                  src={previewUrl}
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  onLoad={() => setIsLoadingFrame(false)}
                  title="Preview"
                />
              </div>
            </div>
          </div>
        ) : (
          <StoppedState
            message={
              !hasSelection
                ? "Select a project to preview its dev environment."
                : status === "not_created"
                ? "No preview environment yet. Click Start to create one."
                : status === "stopped"
                ? "Preview environment is stopped. Click Start to resume."
                : "Environment state is unknown. Refresh and check logs."
            }
          />
        )}
      </div>
    </div>
  );
}

// ── Local Preview Panel ──────────────────────────────────────────────

interface LocalPreviewPanelProps {
  selectedProject?: import("@/stores/worldStore").Project;
  localProjects?: import("@/stores/worldStore").Project[];
  onSelectProject?: (id: string) => void;
}

function LocalPreviewPanel({ selectedProject, localProjects = [], onSelectProject }: LocalPreviewPanelProps) {
  const {
    serverRunning,
    serverPort,
    detectedStack,
    logs,
    serverCommand,
    detectStack,
    startServer,
    stopServer,
  } = useLocalPreviewStore();

  const [customCommand, setCustomCommand] = useState("");
  const [isDetecting, setIsDetecting] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState(false);
  const [deviceMode, setDeviceMode] = useState<DeviceMode>("desktop");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const logEndRef = useRef<HTMLDivElement>(null);

  const projectPath = resolveProjectPath(selectedProject);
  const preferredProjectCommand = selectedProject?.projectConfig?.devCommand
    ? applySubdirectoryToCommand(selectedProject.projectConfig.devCommand, selectedProject)
    : "";

  // Reset state when selected project changes
  const prevProjectId = useRef(selectedProject?.id);
  useEffect(() => {
    if (selectedProject?.id !== prevProjectId.current) {
      prevProjectId.current = selectedProject?.id;
      if (serverRunning) {
        void stopServer();
      }
      setCustomCommand(preferredProjectCommand);
      setError(null);
      useLocalPreviewStore.setState({
        detectedStack: null,
        serverCommand: preferredProjectCommand,
      });
    }
  }, [selectedProject?.id, preferredProjectCommand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync detected command into input
  useEffect(() => {
    if (serverCommand && !customCommand) {
      setCustomCommand(serverCommand);
    }
  }, [serverCommand, customCommand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll logs
  useEffect(() => {
    if (showLogs && logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [logs, showLogs]);

  const previewUrl = serverPort ? `http://localhost:${serverPort}` : null;

  const deviceWidths: Record<DeviceMode, number | null> = {
    desktop: null,
    tablet: 768,
    mobile: 375,
  };

  const handleDetect = async () => {
    if (!projectPath) return;
    setIsDetecting(true);
    setError(null);
    try {
      const stack = await detectStack(projectPath);
      setCustomCommand(stack.dev_command);
    } catch (err) {
      setError(String(err));
    } finally {
      setIsDetecting(false);
    }
  };

  const handleStart = async () => {
    if (!projectPath) return;
    setIsStarting(true);
    setError(null);
    try {
      await startServer(projectPath, customCommand || undefined);
    } catch (err) {
      setError(String(err));
    } finally {
      setIsStarting(false);
    }
  };

  const handleStop = async () => {
    setError(null);
    try {
      await stopServer();
    } catch (err) {
      setError(String(err));
    }
  };

  const handleRefreshFrame = () => {
    if (!iframeRef.current || !previewUrl) return;
    iframeRef.current.src = previewUrl;
  };

  const handleOpenExternal = () => {
    if (!previewUrl) return;
    window.open(previewUrl, "_blank");
  };

  const projectOptions = localProjects.map((p) => ({
    value: p.id.toString(),
    label: p.name,
  }));

  if (!projectPath) {
    return (
      <StoppedState message="No local project found. Create a project with a local path first." />
    );
  }

  return (
    <div className="flex flex-col h-full bg-[var(--background)]">
      {/* Toolbar */}
      <div className="flex items-center gap-2 h-12 px-4 border-b border-colony-border bg-[var(--background)] shrink-0">
        {/* Project Selector */}
        <ColonySelect
          value={selectedProject?.id.toString() ?? ""}
          options={projectOptions}
          placeholder="Select project..."
          onChange={(v) => onSelectProject?.(v)}
          className="w-[180px]"
        />

        <div className="w-px h-4 bg-colony-border mx-1" />

        {/* Detect */}
        <button
          onClick={handleDetect}
          disabled={isDetecting || serverRunning}
          className="flex items-center gap-1.5 h-8 px-2.5 rounded-[6px] bg-colony-surface-elevated hover:bg-colony-surface border border-colony-border text-[13px] font-medium text-colony-text-secondary hover:text-colony-text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shrink-0"
        >
          {isDetecting ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Search size={12} strokeWidth={1.5} />
          )}
          {isDetecting ? "Detecting..." : "Detect"}
        </button>

        {/* Dev command input */}
        <input
          type="text"
          value={customCommand}
          onChange={(e) => setCustomCommand(e.target.value)}
          disabled={serverRunning}
          placeholder="Dev command (e.g. npm run dev)"
          aria-label="Dev command"
          className="h-8 px-2.5 rounded-[6px] bg-[#151518] border border-colony-border focus:border-colony-brand/50 focus:shadow-[0_0_0_3px_rgba(0,178,200,0.1)] text-[12px] font-mono text-colony-text-primary placeholder:text-colony-text-tertiary disabled:opacity-50 outline-none transition-all w-[200px] shrink-0"
        />

        {/* Status */}
        {detectedStack && !serverRunning && !isStarting && (
          <span className="text-[12px] text-colony-text-tertiary truncate">
            {detectedStack.framework} detected
          </span>
        )}
        {isStarting && (
          <span className="text-[12px] text-colony-amber flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-colony-amber animate-pulse" />
            Starting server...
          </span>
        )}
        {serverRunning && !isStarting && (
          <span className="text-[12px] text-colony-green flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-colony-green" />
            Running on port {serverPort}
          </span>
        )}

        <div className="flex-1" />

        {/* Action buttons */}
        {!serverRunning ? (
          <button
            onClick={handleStart}
            disabled={isStarting || isDetecting || !customCommand.trim()}
            className="flex items-center gap-1.5 h-8 px-3 rounded-[6px] bg-colony-brand hover:bg-colony-brand-hover text-white text-[13px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.15)] shrink-0"
          >
            {isStarting ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Play size={11} strokeWidth={2} fill="currentColor" />
            )}
            Start
          </button>
        ) : (
          <>
            <button
              onClick={handleRefreshFrame}
              disabled={!previewUrl}
              className="w-8 h-8 flex items-center justify-center rounded-[6px] text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-colony-surface-elevated transition-all cursor-pointer disabled:opacity-30"
              title="Refresh preview"
            >
              <RotateCw size={13} strokeWidth={1.5} />
            </button>
            <button
              onClick={handleOpenExternal}
              disabled={!previewUrl}
              className="w-8 h-8 flex items-center justify-center rounded-[6px] text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-colony-surface-elevated transition-all cursor-pointer disabled:opacity-30"
              title="Open in browser"
            >
              <ExternalLink size={13} strokeWidth={1.5} />
            </button>
            <button
              onClick={handleStop}
              className="flex items-center gap-1.5 h-8 px-2.5 rounded-[6px] bg-colony-red/15 hover:bg-colony-red/25 text-colony-red text-[13px] font-medium transition-all cursor-pointer shrink-0"
            >
              <Square size={10} strokeWidth={2} fill="currentColor" />
              Stop
            </button>
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-3 rounded-[6px] border border-colony-red/30 bg-colony-red/5 px-3 py-2 text-[12px] text-colony-red">
          {error}
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 relative flex flex-col min-h-0">
        {previewUrl ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            <UrlBar url={`localhost:${serverPort}`} deviceMode={deviceMode} onDeviceChange={setDeviceMode} />
            <div
              className="flex-1 overflow-auto flex justify-center"
              style={{
                backgroundColor: "#0F0F11",
                backgroundImage: "radial-gradient(circle at 1px 1px, #1E1E22 1px, transparent 0)",
                backgroundSize: "20px 20px",
              }}
            >
              <div
                className="h-full bg-white overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  width: deviceWidths[deviceMode] ? `${deviceWidths[deviceMode]}px` : "100%",
                  maxWidth: "100%",
                  ...(deviceWidths[deviceMode]
                    ? { borderLeft: "1px solid #2A2A2F", borderRight: "1px solid #2A2A2F", boxShadow: "0 0 40px rgba(0,0,0,0.3)" }
                    : {}),
                }}
              >
                <iframe
                  ref={iframeRef}
                  src={previewUrl}
                  className="w-full h-full border-0"
                  title="Local Preview"
                />
              </div>
            </div>
          </div>
        ) : (
          <StoppedState
            message={
              !serverRunning && !isStarting
                ? "Click Detect to identify your framework, then Start to launch the dev server."
                : "Starting server, detecting port..."
            }
          />
        )}

        {/* Log panel */}
        {logs.length > 0 && (
          <div className="border-t border-colony-border">
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="w-full flex items-center justify-between px-4 py-1.5 text-[12px] text-colony-text-tertiary hover:bg-colony-surface-elevated/50 transition-colors"
            >
              <span>Server Logs ({logs.length})</span>
              {showLogs ? (
                <ChevronDown size={12} />
              ) : (
                <ChevronUp size={12} />
              )}
            </button>
            {showLogs && (
              <div className="h-[120px] overflow-auto bg-[#0F0F11] px-4 py-2 font-mono text-[11px] text-colony-text-tertiary" style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}>
                {logs.map((line, i) => (
                  <div key={i} className="whitespace-pre-wrap break-all">
                    {line}
                  </div>
                ))}
                <div ref={logEndRef} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Exported Component ───────────────────────────────────────────────

export function PreviewPanel({ isLocalMode, selectedProject, localProjects, onSelectProject }: PreviewPanelProps) {
  if (isLocalMode) {
    return (
      <LocalPreviewPanel
        selectedProject={selectedProject}
        localProjects={localProjects}
        onSelectProject={onSelectProject}
      />
    );
  }
  return <CloudPreviewPanel />;
}
